import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Entity } from "@models/Entity";
import { useAuth } from "@Auth/useAuth";
import {
  EntityImagesClient,
  EntityImageWithUrl,
  ImageEntityType,
} from "@Clients/EntityImagesClient";
import { entityImageKeys } from "./entityImageKeys";

// Cache window sits under the 1h signed-URL TTL so cached URLs stay valid.
const STALE_TIME = 1000 * 60 * 30;

/** Images for an entity, each carrying a resolved signed URL for rendering. */
export function useEntityImages(
  entityType: ImageEntityType,
  entityId: string | undefined
) {
  return useQuery({
    queryKey: entityImageKeys.forEntity(entityType, entityId ?? ""),
    enabled: !!entityId,
    staleTime: STALE_TIME,
    queryFn: async (): Promise<EntityImageWithUrl[]> => {
      const images = await EntityImagesClient.list(entityType, entityId!);
      const paths = images.flatMap((i) =>
        i.thumbnail_path ? [i.storage_path, i.thumbnail_path] : [i.storage_path]
      );
      const urls = await EntityImagesClient.signedUrls(paths);
      return images.map((i) => {
        const url = urls[i.storage_path] ?? null;
        return {
          ...i,
          url,
          // Fall back to the full image when a row predates the thumbnail.
          thumbUrl: i.thumbnail_path ? urls[i.thumbnail_path] ?? url : url,
        };
      });
    },
  });
}

/**
 * Primary thumbnail URL per entity for a set of ids, resolved in one query +
 * one signed-URL batch. Returns a map of entityId → signed URL (or null when an
 * entity has no primary image). For grid/thumbnail surfaces.
 */
export function usePrimaryEntityImages(
  entityType: ImageEntityType,
  entityIds: string[]
) {
  // Sort so the key is stable regardless of incoming order; dedupe upstream.
  const ids = [...entityIds].sort();

  return useQuery({
    queryKey: entityImageKeys.primaries(entityType, ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME,
    queryFn: async (): Promise<Record<string, string | null>> => {
      const byEntity = await EntityImagesClient.listPrimaryForEntities(
        entityType,
        ids
      );
      const rows = Object.values(byEntity);
      // Prefer the lightweight thumbnail; fall back to the full image for rows
      // that predate it.
      const urls = await EntityImagesClient.signedUrls(
        rows.map((r) => r.thumbnail_path ?? r.storage_path)
      );
      // Seed every requested id with null so the returned map is complete —
      // callers can tell "resolved, no image" from "not yet fetched" (an absent
      // key), which lets virtualized grids cache imageless ids without refetch.
      const map: Record<string, string | null> = {};
      for (const id of ids) map[id] = null;
      for (const [entityId, row] of Object.entries(byEntity)) {
        const key = row.thumbnail_path ?? row.storage_path;
        map[entityId] = urls[key] ?? null;
      }
      return map;
    },
  });
}

export function useUploadEntityImage(
  entityType: ImageEntityType,
  entityId: string
) {
  const queryClient = useQueryClient();
  const { baseUser } = useAuth();

  return useMutation({
    meta: { entity: Entity.IMAGES, action: "create" },
    mutationFn: (params: {
      file: File;
      thumbnail?: File | null;
      width?: number;
      height?: number;
      isPrimary?: boolean;
      sortOrder?: number;
    }) =>
      EntityImagesClient.upload({
        organizationId: baseUser!.organization_id!,
        entityType,
        entityId,
        ...params,
      }),
    // Invalidate the whole namespace so the entity's own gallery AND any grid
    // thumbnail (primaries) query refresh together.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: entityImageKeys.all }),
  });
}

export function useDeleteEntityImage() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { entity: Entity.IMAGES, action: "delete" },
    mutationFn: (image: {
      id: string;
      storage_path: string;
      thumbnail_path: string | null;
    }) => EntityImagesClient.remove(image),
    // Invalidate the whole namespace so the entity's own gallery AND any grid
    // thumbnail (primaries) query refresh together.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: entityImageKeys.all }),
  });
}

export function useSetPrimaryEntityImage(
  entityType: ImageEntityType,
  entityId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { entity: Entity.IMAGES, action: "update" },
    mutationFn: (imageId: string) =>
      EntityImagesClient.setPrimary(entityType, entityId, imageId),
    // Invalidate the whole namespace so the entity's own gallery AND any grid
    // thumbnail (primaries) query refresh together.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: entityImageKeys.all }),
  });
}
